import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import { BASE_URL } from "../Constants";
import ShowOneContentComponent from "./ShowOneContentComponent";
import ContentSkeleton from "./ContentSkeleton";
import FooterComponent from "./FooterComponent";

export function ShowOneContentService({ printMode = false }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sid = searchParams.get("sid");
  const [shouldLoadRelated, setShouldLoadRelated] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const { data, isLoading, isError } = useQuery(
    ["content", id],
    async () => {
      const response = await axios.get(BASE_URL + `/api/content/v3/${id}`, {
        params: { sid },
        credentials: "include",
      });
      return response.data;
    },
    {
      staleTime: 600000,
    }
  );

  const {
    data: relatedItems = [],
    isLoading: relatedLoading,
  } = useQuery(
    ["content-related", id],
    async () => {
      const response = await axios.get(BASE_URL + `/api/content/v3/${id}/related`, {
        withCredentials: true,
      });
      return response.data;
    },
    {
      enabled: Boolean(id) && shouldLoadRelated && !printMode,
      staleTime: 600000,
    }
  );

  useEffect(() => {
    if (!data) {
      return undefined;
    }

    const title = data.titlePrimary.split(".").pop();
    document.title = data.titleSecondary
      ? `${title} | ${data.titleSecondary} Lyrics | Gangadwar.in | Gangadwar Bhajnawali`
      : `${title} Lyrics | Gangadwar.in | Gangadwar Bhajnawali`;

    const analyticsUrl = `${BASE_URL}/api/content/v3/analytics/${id}${
      sid ? `?sid=${encodeURIComponent(sid)}` : ""
    }`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(analyticsUrl);
    } else {
      window.setTimeout(() => {
        axios.post(
          analyticsUrl,
          {},
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        ).catch(() => {});
      }, 0);
    }

    return undefined;
  }, [data, id, sid]);

  useEffect(() => {
    setShouldLoadRelated(false);

    if (!id || printMode) {
      return undefined;
    }

    const scheduleRelatedLoad = () => {
      window.setTimeout(() => setShouldLoadRelated(true), 300);
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(scheduleRelatedLoad, {
        timeout: 1200,
      });
      return () => window.cancelIdleCallback(idleId);
    }

    const timerId = window.setTimeout(() => setShouldLoadRelated(true), 500);
    return () => window.clearTimeout(timerId);
  }, [id, printMode]);

  useEffect(() => {
    if (!printMode || !data) {
      return undefined;
    }

    const printTimer = window.setTimeout(() => {
      window.print();
    }, 500);

    return () => {
      window.clearTimeout(printTimer);
    };
  }, [data, printMode]);

  return (
    <>
      {isLoading ? (
        <>
          <ContentSkeleton printMode={printMode} />
          {!printMode && <FooterComponent />}
        </>
      ) : isError ? (
        <p className="text-center py-5">Error fetching data</p>
      ) : (
        <>
          <ShowOneContentComponent
            data={data}
            relatedItems={relatedItems}
            relatedLoading={relatedLoading}
            printMode={printMode}
          />
          {!printMode && <FooterComponent />}
        </>
      )}
    </>
  );
}
